import { inject, Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';

import { AnalyticsService } from 'src/app/components/logging/analytics.service';
import { PlaylistComponentService } from '../services/playlist-component.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { StorageManagerService } from 'src/app/storage/services/storage-manager.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { StorageUtilsService } from 'src/app/storage/services/storage-utils.service';
import { ComponentsService } from '../../services/components.service';
import { ImageDimensionsService } from './image-dimensions.service';

@Injectable({
  providedIn: 'root',
})
export class MediaSelectorService {
  public static readonly FOLDER_COMPONENT = 'rise-folder';
  public static readonly FOLDER_TYPE = {
    STORAGE_FOLDER: 'storage-folder',
    STORAGE_FILE: 'storage-file',
  };

  private playlistComponentService = inject(PlaylistComponentService);
  private storageUtilsService = inject(StorageUtilsService);
  private analyticsFactory = inject(AnalyticsService);
  private templateEditorService = inject(TemplateEditorService);
  private storageManagerService = inject(StorageManagerService);
  private companyStateService = inject(CompanyStateService);
  private componentsService = inject(ComponentsService);
  private imageDimensionsService = inject(ImageDimensionsService);

  onMediaUpdate;
  onUserComponentAdd;

  _getType(file) {
    return this.storageUtilsService.isFolder(file.name)
      ? MediaSelectorService.FOLDER_TYPE.STORAGE_FOLDER
      : MediaSelectorService.FOLDER_TYPE.STORAGE_FILE;
  }

  _createBasicItem(type: string): any {
    return {
      tagName: MediaSelectorService.FOLDER_COMPONENT,
      'play-until-done': true,
      attributes: {
        type,
        environment: environment.production ? 'prod' : 'test',
      },
    };
  }

  private _trackFolderUsage(folderType) {
    this.analyticsFactory.track('Folder Component Updated', {
      folderType: folderType,
      companyId: this.companyStateService.getSelectedCompanyId(),
      presentationId: this.templateEditorService.presentation.id,
      presentationName: this.templateEditorService.presentation.name,
    });
  }

  async storageFilesSelected(
    selectedItems?: any[],
    fileType?: string,
  ): Promise<void> {
    if (!selectedItems || selectedItems.length === 0) {
      return;
    }

    if (this.onMediaUpdate) {
      const filePath = this.storageUtilsService.getFilePath(selectedItems[0]);
      const type = this._getType(selectedItems[0]);

      this.onMediaUpdate(filePath, type);

      this._trackFolderUsage(type);
    } else {
      await this._addSelected(selectedItems, fileType);
    }

    this.closeMediaSelector();
  }

  private closeMediaSelector(): void {
    const currentPage = this.componentsService.getCurrentPage();
    const currentType = currentPage?.type || currentPage?.tagName;

    if (currentType === 'rise-media-selector') {
      this.componentsService.showPreviousPage();
    } else {
      this.componentsService.removePageFromStack('rise-media-selector');
    }
  }

  _addItems(items, fileType?: string) {
    const editItem = items[0] && items.length === 1;

    if (this.onUserComponentAdd) {
      return this.onUserComponentAdd(items, editItem, fileType);
    }

    if (this.playlistComponentService.onAddHandler) {
      this.playlistComponentService.onAddHandler(items, editItem);
    }
  }

  async _addSelected(selectedItems: any[], fileType?: string): Promise<void> {
    const items = [];
    // Playlist items have no independent box geometry — computing one for
    // them is wasted work and would persist dead position data. Hugging
    // only ever applies to standalone components, i.e. when this add is
    // headed for onUserComponentAdd rather than the playlist's onAddHandler.
    const isStandaloneAdd = !!this.onUserComponentAdd;

    for (const item of selectedItems) {
      const type = this._getType(item);
      const newItem = this._createBasicItem(type);
      const isImage = this.storageManagerService.isImage(item.name);

      if (isImage) {
        newItem['play-until-done'] = false;
      }

      const source = this.storageUtilsService.getFilePath(item);
      newItem.attributes.source = source;

      if (isImage && isStandaloneAdd) {
        // Native dimensions give new images a box that hugs their aspect
        // ratio instead of falling through to the generic default box.
        const customization =
          await this.imageDimensionsService.getHuggingBox(source);

        if (customization) {
          newItem.attributes.customization = customization;
        }
      }

      items.push(newItem);

      this._trackFolderUsage(type);
    }

    this._addItems(items, fileType);
  }
}
